import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, GraduationCap, MapPin, MonitorSmartphone, Plus, Trash2, Users } from 'lucide-react';
import { SportsAcademy } from '@/types';
import { sportsApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PriceDisplay } from '@/components/public/PriceDisplay';

export const AdminSportsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<SportsAcademy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const fetchSports = async () => {
      try {
        const data = await sportsApi.getAllSports({ signal: controller.signal });
        setItems(data);
      } catch (fetchError) {
        if ((fetchError as Error).name !== 'AbortError') {
          setError('Sports academies are not available right now. Please try again soon.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchSports();
    return () => controller.abort();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this sports academy?')) return;

    try {
      await sportsApi.deleteSports(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (deleteError) {
      console.error('Failed to delete sports academy:', deleteError);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="page-heading">Sports Academies</h1>
          <p className="page-subtitle">
            Manage academy listings with cleaner audience, level, location, and program presentation.
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate('/admin/sports/new')}>
          <Plus className="h-4 w-4" />
          Add Sports
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="h-32 animate-pulse rounded-[1.75rem] bg-white/70" />
          ))}
        </div>
      ) : error ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">{error}</p>
        </Card>
      ) : items.length === 0 ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">No sports academies found.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <Card key={item.id} className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="flex flex-1 flex-col gap-4 sm:flex-row">
                  <img src={item.coverImageUrl} alt={item.name} className="h-40 w-full rounded-[1.25rem] object-cover sm:h-24 sm:w-36" />
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-semibold text-slate-950">{item.name}</h2>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">{item.about}</p>
                    <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                      <p className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {item.area ? `${item.area}, ` : ''}{item.city}
                      </p>
                      <p className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        {item.level || 'Open to All'}
                      </p>
                      <p className="flex items-center gap-2">
                        <MonitorSmartphone className="h-4 w-4" />
                        {item.teachingMode || 'Offline'}
                      </p>
                      <p className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {item.audience?.length ? item.audience.join(', ') : 'Open to all athletes'}
                      </p>
                      <PriceDisplay isFree={item.isFree} originalPrice={item.startingPriceText} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 xl:ml-4">
                  <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/sports/${item.id}/edit`)}>
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
